import React from 'react';
import { Card, CardBody } from 'reactstrap';

const StatCardAdvance = ({
    title,
    currentValue = 0,
    previousValue,
    goalValue,
    isInverse = false,
    subtitle,
    icon,
    isCurrency = false,
    isPercentage = false,
    comparisonLabel = "M. anterior"
}) => {
    const normalize = (val) => {
        let num = Number(val) || 0;
        const threshold = isPercentage ? 0.05 : 0.005;
        if (Math.abs(num) < threshold) return 0;
        return num;
    };

    const current = normalize(currentValue);

    // Formata o número para exibição
    const displayValue = isCurrency
        ? current.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
        : isPercentage
        ? `${current.toFixed(1)}%`
        : current;

    // Se temos valor prévio, calculamos
    const hasPrevious = typeof previousValue !== "undefined" && previousValue !== null;
    const prev = normalize(previousValue);

    let percentChange = 0;
    if (Math.abs(prev) >= 0.01) {
        percentChange = ((current - prev) / Math.abs(prev)) * 100;
    } else if (Math.abs(current) >= 0.01) {
        percentChange = 100;
    }

    const isPositive = percentChange >= 0;

    // ── Meta / Goal Achievement ──
    const goal = Number(goalValue) || 0;
    const hasGoal = goal >= 0.01;
    let achievementPct = 0;
    if (hasGoal) {
        achievementPct = isInverse
            ? (current <= goal ? 100 : (goal / (Math.abs(current) >= 0.01 ? current : 1)) * 100)
            : (current / goal) * 100;
    }

    const formatGoalDisplay = (val) => {
        if (isCurrency) return val.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
        if (isPercentage) return `${val}%`;
        return val;
    };

    // Determine the progress bar color and fill color matching the image
    let barColor = "#e2661c"; // Default orange
    const titleUpper = title.toUpperCase();
    if (titleUpper.includes("LÍQUIDO") && current < 0) {
        barColor = "#f46a6a"; // Red for negative growth
    } else if (titleUpper.includes("CANCELAMENTOS")) {
        barColor = "#f46a6a"; // Red for cancellations
    } else if (titleUpper.includes("RETORNOS")) {
        barColor = "#c49a6c"; // Golden-brown for returns
    }

    // Determine the icon styles matching the premium theme
    let iconBg = "rgba(226, 102, 28, 0.08)";
    let iconBorder = "rgba(226, 102, 28, 0.20)";
    let iconColor = "#e2661c";

    if (titleUpper.includes("LÍQUIDO") && current < 0) {
        iconBg = "rgba(244, 106, 106, 0.06)";
        iconBorder = "rgba(244, 106, 106, 0.15)";
        iconColor = "#f46a6a";
    } else if (titleUpper.includes("CANCELAMENTOS")) {
        iconBg = "rgba(244, 106, 106, 0.06)";
        iconBorder = "rgba(244, 106, 106, 0.15)";
        iconColor = "#f46a6a";
    } else if (titleUpper.includes("RETORNOS")) {
        iconBg = "rgba(196, 154, 108, 0.06)";
        iconBorder = "rgba(196, 154, 108, 0.15)";
        iconColor = "#c49a6c";
    }

    return (
        <Card 
            className="neo-stat-card shadow-none overflow-hidden h-100" 
            style={{ background: "#fff" }}
            title={subtitle}
        >
            <CardBody className="neo-stat-card-body d-flex flex-column h-100">
                {/* Header Section: Icon on Left, Title on Right */}
                <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                        style={{ 
                            width: "32px", 
                            height: "32px", 
                            minWidth: "32px",
                            borderRadius: "8px",
                            background: iconBg,
                            border: `1px solid ${iconBorder}`,
                            color: iconColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <i className={`${icon} font-size-14`}></i>
                    </div>
                    <p className="neo-stat-card-title">
                        {title}
                    </p>
                </div>

                {/* Main Value and Goal Section */}
                <div className="mb-2">
                    <h4 className="neo-stat-card-value">
                        {displayValue}
                    </h4>
                    <div className="neo-stat-card-meta">
                        {hasGoal ? (
                            <span>
                                Meta: <strong style={{ color: "#482712", fontWeight: 800 }}>{formatGoalDisplay(goal)}</strong>
                            </span>
                        ) : (
                            <span>&nbsp;</span>
                        )}
                    </div>
                </div>

                {/* Bottom Section: Progress Bar and Comparison Badge pushed to the bottom */}
                <div className="mt-auto pt-2">
                    {/* Progress Bar Section */}
                    {hasGoal && (
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <div 
                                className="flex-grow-1" 
                                style={{ 
                                    height: "6px", 
                                    background: "#fef5ef", 
                                    borderRadius: "50px", 
                                    overflow: "hidden" 
                                }}
                            >
                                <div 
                                    style={{ 
                                        height: "100%", 
                                        width: `${Math.max(0, Math.min(100, Math.abs(achievementPct)))}%`, 
                                        background: barColor, 
                                        borderRadius: "50px" 
                                    }} 
                                />
                            </div>
                            <span 
                                style={{ 
                                    fontSize: "10.5px", 
                                    fontWeight: 900, 
                                    color: barColor, 
                                    minWidth: "30px", 
                                    textAlign: "right",
                                    fontFamily: "Outfit, sans-serif"
                                }}
                            >
                                {achievementPct >= 0 ? "" : "-"}{Math.abs(achievementPct).toFixed(0)}%
                            </span>
                        </div>
                    )}

                    {/* Comparison Badge Section */}
                    {hasPrevious && (
                        <div className="d-flex align-items-center gap-1.5 pt-1 flex-wrap">
                            <div className="neo-stat-card-badge">
                                {isPositive ? "↑" : "↓"} {isPositive ? "+" : ""}{percentChange.toFixed(1)}%
                            </div>
                            <span className="neo-stat-card-comparison-label">
                                {comparisonLabel.toLowerCase().includes("mesmo") 
                                    ? "vs. mesmo período" 
                                    : "vs. mês ant."}
                            </span>
                        </div>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default StatCardAdvance;
